import { Entry } from 'contentful';
import { TypeImageSkeleton } from 'types/contentful/TypeImage';

/**
 * Normalizes a URL to ensure it has a proper protocol (https://)
 * Handles protocol-relative URLs (//) by converting them to https://
 */
const normalizeImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // If URL already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If URL is protocol-relative (starts with //), add https:
  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  // Otherwise, assume https://
  return `https://${url}`;
};

const getImageSrc = (image?: Entry<TypeImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>) => {
  if (!image) return null;

  const url = image?.fields.url || image?.fields.file?.fields?.file?.url;
  return normalizeImageUrl(url);
};

const getImageData = (image?: Entry<TypeImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>) => {
  if (!image) return { src: null, alt: null };

  const url = image?.fields.url || image?.fields.file?.fields?.file?.url;

  return {
    src: normalizeImageUrl(url),
    alt: image?.fields.altText || image?.fields.internalName || null,
  };
};

export type ImageData = ReturnType<typeof getImageData>;

/** Extract { src, alt } from a resolved Contentful Asset (e.g. icon on link/linkSet). */
const getAssetIconData = (asset?: {
  fields?: { file?: { url?: string }; description?: string; title?: string };
}): { src: string; alt: string | null } | undefined => {
  const url = asset?.fields?.file?.url;
  if (!url) return undefined;
  return {
    src: url.startsWith('//') ? `https:${url}` : url,
    alt: asset?.fields?.description || asset?.fields?.title || null,
  };
};

export default getImageSrc;
export { getImageData, getAssetIconData };
